import React from 'react';
import {
  Box,
  Button,
  ButtonSelect,
  Icon,
  InlineMessage,
  Menu,
  type MenuOption,
  Tooltip,
} from '@saviynt/design-system';

import { useIspmContext } from '../../../../context';
import { useChatOverlayContext } from '../../../../context/chatOverlayContext';
import { type SaveChartRequest } from '../../../../data-layer/types';
import useDashboardApi from '../../../../hooks/use-dashboardapi';
import { getSaviApiUrlLists, Logger } from '../../../../services';
import { useInvertedLocalize } from '../../../../utils/localization';
import { invertedMessages } from '../../../../utils/messages';

import './SaveToDashboard.css';

interface SaveToDashboardProps {
  chartName: string;
}

export default function SaveToDashboard({ chartName }: SaveToDashboardProps) {
  const { chatOverlayState } = useChatOverlayContext();

  const l = useInvertedLocalize(invertedMessages);
  const { saveChart } = useDashboardApi();
  const { ispmState } = useIspmContext();

  const handleSaveChart = async (dashboard: MenuOption) => {
    const data = chatOverlayState.contentPanelData;

    if (!dashboard.value || !('chartSpec' in data)) {
      Logger.warn('No dashboard to save this chart');

      return;
    }

    const request: SaveChartRequest = {
      dashboard_id: +dashboard.value,
      datasource_id: data.datasource_id,
      chart_name: chartName,
      chartSpec: data.chartSpec,
    };

    const { DASHBOARD_URL } = getSaviApiUrlLists(ispmState.useMockServerApis);

    try {
      await saveChart(request, DASHBOARD_URL);
    } catch (err) {
      // TODO: https://saviyntars.atlassian.net/browse/IN-12515
      // Handle error as confirmed by PO.right now, its not finalised.
      Logger.error({ err });
    }
  };

  function getCustomContent() {
    return (
      <div>
        <InlineMessage
          text={l(
            'Dashboards unavailable. Try refreshing the page to connect to our dashboards list.'
          )}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
        <Box className='flexBox'>
          <Button
            type='button'
            kind='ghost'
            size='small'
            onClick={() => window.location.reload()}
            leftIcon={
              <Icon kind='Refresh' size='medium' color='neutral-100' />
            }>
            {l('Refresh')}
          </Button>
        </Box>
      </div>
    );
  }

  return (
    <Menu
      options={chatOverlayState.dashboardLists.map((ele) => ({
        label: ele.name,
        value: ele.id.toString(),
        isDisabled: false,
        isChecked: false,
      }))}
      // By default one dashboard is available. If not, show error
      CustomMenu={
        chatOverlayState.dashboardLists.length === 0
          ? getCustomContent()
          : undefined
      }
      onChange={async (data: MenuOption) => {
        Logger.info('User selected dashboard - ', { data });
        await handleSaveChart(data);
      }}
      className='Ispm-SaveToDashboard-Menu'
      trigger={
        <Tooltip
          text={l('Save to Dashboard')}
          placement='top'
          trigger={
            <ButtonSelect
              kind='icon'
              size='medium'
              prefixIcon={
                <Icon
                  kind='Dashboard'
                  size='medium'
                  color='info-500'
                  className='Ispm-SaveToDashboard-Menu-icon'
                />
              }
              onClick={() => {}}
            />
          }
        />
      }
      size='medium'
      minOptionsForSearch={0}
    />
  );
}
